import { apiGet, apiPost } from '../utils/apiUtils';

/**
 * Usage Service - Handles user credit and tier management
 * 
 * This service provides functions to interact with the usage management API
 * for checking credits, getting usage statistics, and managing user tiers.
 */

/**
 * Get current user's usage statistics
 * @returns {Promise<Object>} Usage stats including credits available, used, tier info
 */
export async function getUsageStats() {
  try {
    const response = await apiGet('/api/usage/stats');
    if (!response.ok) {
      throw new Error(`Failed to fetch usage stats: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching usage stats:', error);
    throw error;
  }
}

/**
 * Check if user has sufficient credits for an operation
 * @param {number} creditsNeeded - Number of credits required
 * @returns {Promise<Object>} Credit check result with hasCredits boolean
 */
export async function checkCredits(creditsNeeded) {
  try {
    const response = await apiPost('/api/usage/check-credits', { creditsNeeded });
    if (!response.ok) {
      throw new Error(`Failed to check credits: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error checking credits:', error);
    throw error;
  }
}

/**
 * Get current user's tier information
 * @returns {Promise<Object>} Tier details including limits and features
 */
export async function getUserTier() {
  try {
    const response = await apiGet('/api/usage/tier');
    if (!response.ok) {
      throw new Error(`Failed to fetch user tier: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching user tier:', error);
    throw error;
  }
}

/**
 * Get available features for current user's tier
 * @returns {Promise<Object>} Available features and limits
 */
export async function getUserFeatures() {
  try {
    const response = await apiGet('/api/usage/features');
    if (!response.ok) {
      throw new Error(`Failed to fetch user features: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching user features:', error);
    throw error;
  }
}

/**
 * Get usage analytics (admin only)
 * @param {number} days - Number of days to analyze (default: 30)
 * @returns {Promise<Object>} Usage analytics data
 */
export async function getUsageAnalytics(days = 30) {
  try {
    const response = await apiGet(`/api/usage/analytics?days=${days}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch usage analytics: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching usage analytics:', error);
    throw error;
  }
}

/**
 * Format credit count for display
 * @param {number} credits - Number of credits
 * @returns {string} Formatted credit display
 */
export function formatCredits(credits) {
  // Handle null, undefined, or non-numeric values
  if (credits === null || credits === undefined || isNaN(credits)) {
    return '0';
  }
  
  if (credits === -1) return 'Unlimited';
  if (credits >= 1000) {
    return `${(credits / 1000).toFixed(1)}k`;
  }
  return credits.toString();
}

/**
 * Get credit usage percentage
 * @param {number} creditsUsed - Credits used
 * @param {number} creditsTotal - Total credits available
 * @returns {number} Percentage used (0-100)
 */
export function getUsagePercentage(creditsUsed, creditsTotal) {
  // Handle null, undefined, or non-numeric values
  if (creditsUsed === null || creditsUsed === undefined || isNaN(creditsUsed)) {
    creditsUsed = 0;
  }
  if (creditsTotal === null || creditsTotal === undefined || isNaN(creditsTotal)) {
    creditsTotal = 0;
  }
  
  if (creditsTotal === -1) return 0; // Unlimited
  if (creditsTotal === 0) return 100;
  return Math.round((creditsUsed / creditsTotal) * 100);
}

/**
 * Get warning level based on credit usage
 * @param {number} percentage - Usage percentage
 * @returns {string} Warning level: 'none', 'low', 'critical'
 */
export function getWarningLevel(percentage) {
  if (percentage >= 90) return 'critical';
  if (percentage >= 75) return 'low';
  return 'none';
}

/**
 * Get display color based on usage percentage
 * @param {number} percentage - Usage percentage
 * @returns {string} Material-UI color: 'success', 'warning', 'error'
 */
export function getUsageColor(percentage) {
  if (percentage >= 90) return 'error';
  if (percentage >= 75) return 'warning';
  return 'success';
}